import * as React from 'react';

import { BrokeredOrderRecord } from '@rematter/modules/brokerage/types/brokeredOrder';
import { colorByBrokeOrderStatus } from '@rematter/modules/brokerage/utils/status';
import { Badge, DataGridColumn } from '@rematter/ui-web';
import { cn, formatMoney } from '@rematter/ui-web/lib';

import { useTranslations } from '../../../i18n/useTranslations';
import { BrokeredOrderActionMenu } from '../BrokeredOrderActionMenu';

type Props = {
  onRowClick: (row: BrokeredOrderRecord) => void;
  onVoid?: (id: string) => void;
  onFinish?: (id: string) => void;
  onRefetch?: () => void;
};

export const useBrokeredOrderListColumns = ({
  onRowClick,
  onVoid,
  onFinish,
  onRefetch,
}: Props) => {
  const t = useTranslations();

  const handleCellDoubleClick = React.useCallback(
    ({ row }: { row: BrokeredOrderRecord }) => {
      onRowClick(row);
    },
    [onRowClick],
  );

  const columns: DataGridColumn<BrokeredOrderRecord>[] = React.useMemo(
    () => [
      {
        key: 'orderNumber',
        header: t.orders.table.columns.orderNumber,
        sortable: true,
        width: 120,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <div className="flex min-h-8 items-center text-left">
            {row.orderNumber}
          </div>
        ),
      },
      {
        key: 'status',
        header: t.orders.table.columns.status,
        sortable: true,
        width: 100,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <Badge color={colorByBrokeOrderStatus[row.status]} size="small">
            {row.status}
          </Badge>
        ),
      },
      {
        key: 'supplier',
        header: t.orders.table.columns.supplier,
        width: 180,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <div className="text-left">{row.supplier?.name ?? '-'}</div>
        ),
      },
      {
        key: 'customer',
        header: t.orders.table.columns.customer,
        width: 180,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <div className="text-left">{row.customer?.name ?? '-'}</div>
        ),
      },
      {
        key: 'lineItemCount',
        header: t.orders.table.columns.lineItems,
        width: 100,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <div className="text-center">{row.lineItemCount}</div>
        ),
      },
      {
        key: 'purchaseTotal',
        header: t.orders.table.columns.purchase,
        width: 120,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <div className="text-right">
            {row.purchaseTotal
              ? formatMoney(
                  row.purchaseTotal.amount,
                  row.purchaseTotal.currencyCode,
                )
              : '\u2014'}
          </div>
        ),
      },
      {
        key: 'salesTotal',
        header: t.orders.table.columns.sales,
        width: 120,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <div className="text-right">
            {row.salesTotal
              ? formatMoney(row.salesTotal.amount, row.salesTotal.currencyCode)
              : '\u2014'}
          </div>
        ),
      },
      {
        key: 'margin',
        header: t.orders.table.columns.margin,
        width: 120,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => {
          const marginAmount = row.margin?.amount ?? 0;
          const marginPercent = Number(row.marginPercent ?? 0);
          return (
            <div
              className={cn(
                'text-right',
                row.margin && marginAmount >= 0 && 'text-success',
                row.margin && marginAmount < 0 && 'text-failure',
              )}
            >
              {row.margin
                ? `${formatMoney(row.margin.amount, row.margin.currencyCode)} (${marginPercent.toFixed(1)}%)`
                : '\u2014'}
            </div>
          );
        },
      },
      {
        key: 'actions',
        header: '',
        width: 60,
        pinned: 'right',
        cell: ({ row }) => (
          <BrokeredOrderActionMenu
            order={row}
            onVoid={onVoid}
            onFinish={onFinish}
            onRefetch={onRefetch}
          />
        ),
      },
    ],
    [t, handleCellDoubleClick, onRowClick, onVoid, onFinish, onRefetch],
  );

  const columnVisibilityState = React.useMemo(
    () => Object.fromEntries(columns.map((col) => [col.key, true])),
    [columns],
  );

  return { columns, columnVisibilityState };
};
